import { findAncestorState } from '../common/ancestor';
import { disabledControlClasses } from '../common/shared-classes';
import uuidv4 from '../utils/uuid';
import { ChevronRight, createSvg } from './../common/icons';
export default function (Alpine) {
  Alpine.directive('h-sidebar', (el, { modifiers }, { cleanup }) => {
    el.classList.add('group/sidebar', 'bg-sidebar', '[--badge-ring:var(--sidebar)]', 'text-sidebar-foreground', 'border-sidebar-border', 'vbox', 'h-full', 'w-(--sidebar-width,16rem)', 'data-[collapsed=true]:w-min');

    function setBorder() {
      if (el.getAttribute('data-borderless') === 'true') {
        el.classList.remove('border-r', 'border-l');
      } else {
        if (modifiers.includes('right')) {
          el.classList.add('border-l');
          el.classList.remove('border-r');
        } else {
          el.classList.add('border-r');
          el.classList.remove('border-l');
        }
      }
    }

    setBorder();

    el.setAttribute('data-slot', 'sidebar');

    const setFloating = () => {
      if (el.getAttribute('data-floating') === 'true') {
        el.classList.add('border', 'rounded-lg', 'shadow-sm');
      } else {
        el.classList.remove('border', 'rounded-lg', 'shadow-sm');
      }
    };

    const setElevated = () => {
      if (el.getAttribute('data-elevated') === 'true') {
        el.classList.add('border-x', 'shadow-sm');
      } else {
        el.classList.remove('border-x', 'shadow-sm');
      }
    };

    if (el.hasAttribute('data-floating')) setFloating();
    else if (el.hasAttribute('data-elevated')) setElevated();
    else if (el.hasAttribute('data-borderless')) setBorder();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-elevated') setElevated();
        else if (mutation.attributeName === 'data-floating') setFloating();
        else setBorder();
      });
    });

    observer.observe(el, { attributes: true, attributeFilter: ['data-floating', 'data-elevated', 'data-borderless'] });

    cleanup(() => {
      observer.disconnect();
    });
  });

  Alpine.directive('h-sidebar-header', (el) => {
    el.classList.add('vbox', 'gap-2', 'p-2');
    if (el.getAttribute('data-borderless') !== 'true') el.classList.add('inset-shadow-[0_-1px_var(--sidebar-border)]');
    el.setAttribute('data-slot', 'sidebar-header');
  });

  Alpine.directive('h-sidebar-header-item', (el) => {
    el.classList.add(
      'hbox',
      'w-full',
      'items-center',
      'gap-2',
      'px-2',
      'py-1',
      'overflow-hidden',
      'rounded-md',
      'text-left',
      'text-base',
      'leading-none',
      'font-semibold',
      'align-middle',
      '[&>span]:align-middle',
      '[&>span]:truncate',
      '[&>div]:min-w-0',
      'svg-defaults',
      '[&>svg:not(:first-child):last-child]:ml-auto',
      'group-data-[collapsed=true]/sidebar:has-[>svg:first-child]:p-0!',
      'group-data-[collapsed=true]/sidebar:has-[>[data-slot=avatar]:first-child]:p-0!',
      'group-data-[collapsed=true]/sidebar:[&>svg:first-child]:size-8!',
      'group-data-[collapsed=true]/sidebar:[&>[data-slot=avatar]:first-child]:size-8!',
      'group-data-[collapsed=true]/sidebar:[&>*:not(svg:first-child):not([data-slot=menu]):not([data-slot=avatar]:first-child)]:hidden!'
    );

    if (el.tagName === 'BUTTON' || el.tagName === 'A') {
      if (el.tagName === 'BUTTON') el.setAttribute('type', 'button');
      el.classList.add('cursor-pointer', 'outline-hidden', 'ring-sidebar-ring', 'focus-visible:ring-[calc(var(--spacing)*0.75)]');
    }

    el.setAttribute('data-slot', 'sidebar-header-item');
  });

  Alpine.directive('h-sidebar-content', (el) => {
    el.classList.add('vbox', 'min-h-0', 'flex-1', 'overflow-auto', 'scrollbar-none');
    el.setAttribute('data-slot', 'sidebar-content');
  });

  Alpine.directive('h-sidebar-group', (el, { expression, modifiers }, { effect, evaluate, evaluateLater, Alpine }) => {
    el.classList.add('relative', 'vbox', 'w-full', 'min-w-0', 'p-2');
    el.setAttribute('data-slot', 'sidebar-group');
    el._h_sidebar_group = {
      collapsable: modifiers.includes('collapsed'),
      controlId: undefined,
      controls: undefined,
      state: Alpine.reactive({
        collapsed: evaluate(expression || 'false'),
      }),
    };
    if (expression) {
      el._h_sidebar_group.state = Alpine.reactive({
        collapsed: evaluate(expression || 'false'),
      });
      const getCollapsed = evaluateLater(expression);
      effect(() => {
        getCollapsed((collapsed) => {
          el._h_sidebar_group.state.collapsed = collapsed;
        });
      });
    }
  });

  Alpine.directive('h-sidebar-group-label', (el, { original }, { cleanup, effect }) => {
    const group = findAncestorState(Alpine, el, '_h_sidebar_group');
    if (!group) {
      throw new Error(`${original} must be placed inside a sidebar group`);
    }
    el.classList.add(
      'group/sidebar-group-label',
      'ring-sidebar-ring',
      'flex',
      'h-8',
      'shrink-0',
      'items-center',
      'gap-1',
      'rounded-md',
      'px-2',
      'has-[[data-slot=sidebar-group-actions]]:pr-1.5',
      'font-medium',
      'outline-hidden',
      'transition-[margin,opacity]',
      'motion-reduce:transition-none',
      'duration-200',
      'ease-linear',
      'focus-visible:ring-[calc(var(--spacing)*0.75)]',
      '[&>svg]:size-4',
      '[&>svg]:shrink-0',
      'group-data-[collapsed=true]/sidebar:hidden!'
    );
    el.setAttribute('data-slot', 'sidebar-group-label');

    if (group._h_sidebar_group.collapsable) {
      el.classList.add('text-sidebar-foreground', 'text-sm', 'hover:bg-sidebar-secondary', 'hover:text-sidebar-secondary-foreground', 'active:bg-sidebar-secondary', 'active:text-sidebar-secondary-foreground');

      // Only a collapsable group turns its label into a control. The plain label
      // in the branch below stays text, with no role and no tab stop.
      const isButton = el.tagName === 'BUTTON';
      if (isButton) {
        el.setAttribute('type', 'button');
      } else {
        el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      }

      if (el.hasAttribute('id')) {
        group._h_sidebar_group.controlId = el.getAttribute('id');
      } else {
        group._h_sidebar_group.controlId = `sgl${uuidv4()}`;
        el.setAttribute('id', group._h_sidebar_group.controlId);
      }
      group._h_sidebar_group.controls = `sgc${uuidv4()}`;
      el.setAttribute('aria-controls', group._h_sidebar_group.controls);

      // Written through an effect rather than once, since a bound expression can
      // collapse the group without the click handler ever running. The arrow
      // turns off this attribute, so it would point the wrong way too.
      effect(() => {
        el.setAttribute('aria-expanded', !group._h_sidebar_group.state.collapsed);
      });

      const handler = () => {
        group._h_sidebar_group.state.collapsed = !group._h_sidebar_group.state.collapsed;
      };

      // A native button activates on both Enter and Space, so an element only
      // playing one has to forward those keys itself.
      const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page and Enter submits a surrounding form, neither
        // of which a button press should do here.
        event.preventDefault();
        el.click();
      };

      el.appendChild(
        createSvg({
          icon: ChevronRight,
          classes: 'ml-auto pointer-events-none size-4 shrink-0 transition-transform motion-reduce:transition-none duration-200 [[aria-expanded=true]>&]:rotate-90',
          attrs: {
            'aria-hidden': true,
            role: 'presentation',
          },
        })
      );

      el.addEventListener('click', handler);
      if (!isButton) el.addEventListener('keydown', onKeyDown);

      cleanup(() => {
        el.removeEventListener('click', handler);
        el.removeEventListener('keydown', onKeyDown);
      });
    } else {
      el.classList.add('text-sidebar-foreground/70', 'text-xs');
    }
  });

  Alpine.directive('h-sidebar-group-actions', (el, { modifiers }) => {
    el.classList.add('ml-auto', 'flex', 'items-center', 'gap-1', 'group-data-[collapsed=true]/sidebar:hidden');
    if (modifiers.includes('autohide')) {
      // Hide only for fine pointers (stays visible on touch); reveal on label hover or focus within.
      // not-sr-only resets margin to 0, so re-assert ml-auto in the revealed states to keep the actions right-aligned.
      el.classList.add('pointer-fine:sr-only', 'focus-within:not-sr-only', 'focus-within:ml-auto', 'group-hover/sidebar-group-label:not-sr-only', 'group-hover/sidebar-group-label:ml-auto');
    }
    el.setAttribute('data-slot', 'sidebar-group-actions');
  });

  Alpine.directive('h-sidebar-group-action', (el) => {
    el.classList.add(
      'text-sidebar-foreground',
      'ring-sidebar-ring',
      'hover:bg-sidebar-secondary',
      'hover:text-sidebar-secondary-foreground',
      'active:bg-sidebar-primary',
      'active:text-sidebar-primary-foreground',
      'flex',
      'aspect-square',
      'w-5',
      'shrink-0',
      'items-center',
      'justify-center',
      'rounded-[30%]',
      'p-0',
      'outline-hidden',
      'transition-transform',
      'motion-reduce:transition-none',
      'focus-visible:ring-[calc(var(--spacing)*0.75)]',
      '[&>svg]:size-4',
      '[&>svg]:shrink-0'
    );
    if (el.tagName !== 'BUTTON') {
      el.setAttribute('role', 'button');
    } else {
      el.setAttribute('type', 'button');
    }
    el.setAttribute('data-slot', 'sidebar-group-action');
  });

  Alpine.directive('h-sidebar-group-content', (el, { original }, { effect }) => {
    const group = findAncestorState(Alpine, el, '_h_sidebar_group');
    if (!group) {
      throw new Error(`${original} must be placed inside a sidebar group`);
    }
    el.classList.add('w-full', 'text-sm', 'data-[collapsed=true]:hidden', 'group-data-[collapsed=true]/sidebar:block!');
    el.setAttribute('data-slot', 'sidebar-group-content');

    if (group._h_sidebar_group.collapsable) {
      el.setAttribute('id', group._h_sidebar_group.controls);
      el.setAttribute('aria-labelledby', group._h_sidebar_group.controlId);
      el.setAttribute('data-collapsed', group._h_sidebar_group.state.collapsed);
      effect(() => {
        el.setAttribute('data-collapsed', group._h_sidebar_group.state.collapsed);
      });
    }
  });

  Alpine.directive('h-sidebar-menu', (el, { original }) => {
    if (el.tagName !== 'UL') {
      throw new Error(`${original} must be an ul element`);
    }
    el.classList.add('vbox', 'w-full', 'min-w-0', 'gap-1');
    el.setAttribute('data-slot', 'sidebar-menu');
  });

  Alpine.directive('h-sidebar-menu-item', (el, { original, expression, modifiers }, { effect, evaluate, evaluateLater, Alpine }) => {
    if (el.tagName !== 'LI') {
      throw new Error(`${original} must be a li element`);
    }
    el._h_sidebar_menu_item = {
      isSub: false,
      collapsable: modifiers.includes('collapsed'),
      controlId: undefined,
      controls: undefined,
      state: Alpine.reactive({
        collapsed: evaluate(expression || 'false'),
      }),
    };
    let parent = el.parentElement;
    while (parent) {
      if (parent.getAttribute('data-slot') === 'sidebar-menu-sub') {
        el._h_sidebar_menu_item.isSub = true;
        break;
      } else if (parent.getAttribute('data-slot') === 'sidebar') {
        break;
      }
      parent = parent.parentElement;
    }
    if (!el._h_sidebar_menu_item.isSub) {
      el.classList.add('group/menu-item', 'relative');
    } else {
      el.classList.add('relative');
    }
    el.setAttribute('data-slot', 'sidebar-menu-item');

    if (expression) {
      const getCollapsed = evaluateLater(expression);
      effect(() => {
        getCollapsed((collapsed) => {
          el._h_sidebar_menu_item.state.collapsed = collapsed;
        });
      });
    }
  });

  // One shared body behind two directive names: 'h-sidebar-menu-nav' is a
  // navigation destination, 'h-sidebar-menu-button' is any other control a
  // sidebar can hold (a trigger, a toggle, a selectable row). They differ only
  // in the 'data-slot' they carry and in who owns 'aria-current'.
  const menuButton =
    (nav) =>
    (el, { original }, { cleanup, effect, Alpine }) => {
      if (el.tagName !== 'BUTTON' && el.tagName !== 'A') {
        throw new Error(`${original} must be a button or a link`);
      } else if (el.tagName === 'BUTTON') {
        el.setAttribute('type', 'button');
      }
      const menuItem = findAncestorState(Alpine, el, '_h_sidebar_menu_item');
      const isSub = !!menuItem?._h_sidebar_menu_item.isSub;
      el.classList.add(
        'flex',
        'w-full',
        'items-center',
        'gap-2',
        'overflow-hidden',
        'rounded-md',
        'align-middle',
        '[&>span]:align-middle',
        'outline-hidden',
        'ring-sidebar-ring',
        'hover:bg-sidebar-secondary',
        'hover:[--badge-ring:var(--sidebar-secondary)]',
        'hover:text-sidebar-secondary-foreground',
        'focus-visible:ring-[calc(var(--spacing)*0.75)]',
        'active:bg-sidebar-primary',
        'active:[--badge-ring:var(--sidebar-primary)]',
        'active:text-sidebar-primary-foreground',
        ...disabledControlClasses,
        'aria-disabled:pointer-events-none',
        'aria-disabled:opacity-disabled',
        'data-[active=true]:bg-sidebar-primary',
        'data-[active=true]:[--badge-ring:var(--sidebar-primary)]',
        'data-[active=true]:text-sidebar-primary-foreground',
        '[&>span]:truncate',
        '[&>div]:min-w-0',
        'svg-defaults'
      );

      const sizes = {
        default: ['h-8', 'text-sm'],
        sm: ['h-7', 'text-xs'],
        lg: ['h-12', 'text-sm'],
      };

      function setSize(size) {
        if (Object.prototype.hasOwnProperty.call(sizes, size)) {
          el.classList.add(...sizes[size]);
        }
      }

      if (!el.hasAttribute('data-slot')) {
        const kind = nav ? 'nav' : 'button';
        el.setAttribute('data-slot', isSub ? `sidebar-menu-sub-${kind}` : `sidebar-menu-${kind}`);
      }

      // 'data-active' is the styling flag, so on its own the current destination is
      // marked by colour alone. On the nav variant 'aria-current' is what says so
      // out loud, matching the menu, the navigation menu and the bottom navigation.
      // The plain menu button never touches 'aria-current', since an active button
      // that is not navigation (a trigger, a filter) is not a current page.
      if (nav) {
        const syncActive = () => {
          if (el.getAttribute('data-active') === 'true') {
            el.setAttribute('aria-current', 'page');
          } else {
            el.removeAttribute('aria-current');
          }
        };

        syncActive();

        const activeObserver = new MutationObserver(syncActive);
        activeObserver.observe(el, { attributes: true, attributeFilter: ['data-active'] });
        cleanup(() => activeObserver.disconnect());
      }

      if (isSub) {
        el.classList.add('text-sidebar-foreground', 'h-7', 'min-w-0', '-translate-x-px', 'px-2', '[&>svg:not(:first-child):last-child]:ml-auto', 'group-data-[collapsed=true]/sidebar:hidden');
      } else {
        el.classList.add(
          'peer/menu-button',
          'text-left',
          'p-2',
          'text-sm',
          'duration-200',
          'transition-[width,height,padding]',
          'motion-reduce:transition-none',
          'group-has-data-[sidebar=menu-action]/menu-item:pr-8',
          'data-[active=true]:font-medium',
          'aria-[expanded=true]:hover:bg-sidebar-secondary',
          'aria-[expanded=true]:hover:text-sidebar-secondary-foreground',
          'group-data-[collapsed=true]/sidebar:size-8!',
          'group-data-[collapsed=true]/sidebar:[&>*:not(svg:first-child):not([data-slot=menu]):not([data-slot=avatar]:first-child)]:hidden!'
        );

        if (el.getAttribute('data-logo') === 'true') {
          el.classList.add('[&>svg:first-child]:rounded-control', '[&>[data-slot=avatar]:first-child]:rounded-control', 'group-data-[collapsed=true]/sidebar:justify-center!');
          if (el.parentElement?.getAttribute('data-slot') !== 'sidebar-header') {
            el.classList.add('pl-1', 'group-data-[collapsed=true]/sidebar:p-1');
            sizes.default.push('[&>[data-slot=avatar]:first-child]:text-xs', '[&>svg:first-child]:size-6!', '[&>[data-slot=avatar]:first-child]:size-6!');
            sizes.sm.push('[&>[data-slot=avatar]:first-child]:text-xs', '[&>svg:first-child]:size-5.5!', '[&>[data-slot=avatar]:first-child]:size-5.5!');
            sizes.lg.push(
              '[&>svg:first-child]:size-10!',
              '[&>[data-slot=avatar]:first-child]:size-10!',
              'group-data-[collapsed=true]/sidebar:[&>[data-slot=avatar]:first-child]:size-6!',
              'group-data-[collapsed=true]/sidebar:[&>[data-slot=avatar]:first-child]:text-xs'
            );
          }
        }
      }

      setSize(el.getAttribute('data-size') || 'default');

      if (menuItem && menuItem._h_sidebar_menu_item.collapsable) {
        if (el.hasAttribute('id')) {
          menuItem._h_sidebar_menu_item.controlId = el.getAttribute('id');
        } else {
          menuItem._h_sidebar_menu_item.controlId = `sgl${uuidv4()}`;
          el.setAttribute('id', menuItem._h_sidebar_menu_item.controlId);
        }
        menuItem._h_sidebar_menu_item.controls = `sgc${uuidv4()}`;
        el.setAttribute('aria-controls', menuItem._h_sidebar_menu_item.controls);

        // Written through an effect rather than once, since a bound expression can
        // collapse the item without the click handler ever running. The arrow
        // turns off this attribute, so it would point the wrong way too.
        effect(() => {
          el.setAttribute('aria-expanded', !menuItem._h_sidebar_menu_item.state.collapsed);
        });

        const handler = () => {
          menuItem._h_sidebar_menu_item.state.collapsed = !menuItem._h_sidebar_menu_item.state.collapsed;
        };

        el.appendChild(
          createSvg({
            icon: ChevronRight,
            classes: 'ml-auto pointer-events-none size-4 shrink-0 transition-transform motion-reduce:transition-none duration-200 [[aria-expanded=true]>&]:rotate-90',
            attrs: {
              'aria-hidden': true,
              role: 'presentation',
            },
          })
        );

        el.addEventListener('click', handler);

        cleanup(() => {
          el.removeEventListener('click', handler);
        });
      } else {
        el.classList.add('[&>svg:not(:first-child):last-child]:ml-auto');
      }
    };

  Alpine.directive('h-sidebar-menu-button', menuButton(false));
  Alpine.directive('h-sidebar-menu-nav', menuButton(true));

  Alpine.directive('h-sidebar-menu-action', (el, { modifiers }) => {
    el.classList.add(
      'text-sidebar-foreground',
      'ring-sidebar-ring',
      'hover:bg-sidebar-secondary',
      'peer-data-[active=true]/menu-button:focus-visible:border',
      'peer-data-[active=true]/menu-button:focus-visible:border-sidebar',
      'active:bg-sidebar-primary',
      'active:text-sidebar-primary-foreground',
      'hover:text-sidebar-secondary-foreground',
      'peer-hover/menu-button:text-sidebar-secondary-foreground',
      'peer-active/menu-button:text-sidebar-primary-foreground',
      'peer-data-[active=true]/menu-button:text-sidebar-primary-foreground',
      'absolute',
      'top-1/2',
      '-translate-y-1/2',
      'right-0.5',
      'flex',
      'aspect-square',
      'h-full',
      'max-h-7',
      'peer-data-[size=sm]/menu-button:max-h-6',
      'items-center',
      'justify-center',
      'rounded-md',
      'p-0',
      'outline-hidden',
      'transition-transform',
      'motion-reduce:transition-none',
      'focus-visible:ring-[calc(var(--spacing)*0.75)]',
      '[&>svg]:size-4',
      '[&>svg]:shrink-0',
      'group-data-[collapsed=true]/sidebar:hidden'
    );
    if (modifiers.includes('autohide')) {
      el.classList.add('group-focus-within/menu-item:opacity-100', 'group-hover/menu-item:opacity-100', 'aria-[expanded=true]:opacity-100', 'pointer-fine:opacity-0');
    }
    if (el.tagName !== 'BUTTON') {
      el.setAttribute('role', 'button');
    } else {
      el.setAttribute('type', 'button');
    }
    el.setAttribute('data-slot', 'sidebar-menu-action');
  });

  Alpine.directive('h-sidebar-menu-badge', (el, { original }) => {
    if (el.tagName !== 'SPAN') {
      throw new Error(`${original} must be a span element`);
    }
    el.classList.add('flex-1', 'pointer-events-none', 'flex', 'h-full', 'min-w-min', 'items-center', 'justify-end', 'text-xs', 'font-medium', 'tabular-nums', 'select-none', 'group-data-[collapsed=true]/sidebar:hidden');
    el.setAttribute('data-slot', 'sidebar-menu-badge');
  });

  Alpine.directive('h-sidebar-menu-skeleton', (el, { modifiers }) => {
    el.classList.add('flex', 'h-8', 'items-center', 'gap-2', 'rounded-md', 'px-2');
    const skeleton = document.createElement('div');
    skeleton.classList.add('h-4', 'flex-1', 'bg-sidebar-secondary', 'animate-pulse', 'rounded-md');
    if (modifiers.includes('icon')) {
      skeleton.classList.add('group-data-[collapsed=true]/sidebar:hidden!');
      const icon = document.createElement('div');
      icon.classList.add('size-4', 'rounded-md', 'bg-sidebar-secondary', 'animate-pulse', 'rounded-md');
      el.appendChild(icon);
    } else {
      skeleton.classList.add('group-data-[collapsed=true]/sidebar:w-4!', 'group-data-[collapsed=true]/sidebar:max-w-4!');
    }
    skeleton.style.maxWidth = `${Math.floor(Math.random() * 40) + 50}%`;
    el.appendChild(skeleton);
    el.setAttribute('data-slot', 'sidebar-menu-skeleton');
  });

  Alpine.directive('h-sidebar-separator', (el) => {
    el.classList.add('bg-sidebar-border', 'shrink-0', 'h-px', 'w-full');
    el.setAttribute('data-slot', 'sidebar-separator');
    el.setAttribute('role', 'none');
  });

  Alpine.directive('h-sidebar-menu-sub', (el, { original }, { effect, Alpine }) => {
    if (el.tagName !== 'UL') {
      throw new Error(`${original} must be an ul element`);
    }
    const menuItem = findAncestorState(Alpine, el, '_h_sidebar_menu_item');
    if (!menuItem) {
      throw new Error(`${original} must be placed inside a sidebar menu item`);
    }
    el.classList.add('vbox', 'min-w-0', 'translate-x-px', 'gap-1', 'pl-2.5', 'py-0.5', 'ml-3.5', 'data-[collapsed=true]:hidden!', 'group-data-[collapsed=true]/sidebar:hidden!');
    if (el.getAttribute('data-line') !== 'false') {
      el.classList.add('border-sidebar-border', 'border-l');
    }
    el.setAttribute('data-slot', 'sidebar-menu-sub');

    if (menuItem._h_sidebar_menu_item.collapsable) {
      el.setAttribute('id', menuItem._h_sidebar_menu_item.controls);
      el.setAttribute('aria-labelledby', menuItem._h_sidebar_menu_item.controlId);
      el.setAttribute('data-collapsed', menuItem._h_sidebar_menu_item.state.collapsed);
      effect(() => {
        el.setAttribute('data-collapsed', menuItem._h_sidebar_menu_item.state.collapsed);
      });
    }
  });

  Alpine.directive('h-sidebar-footer', (el) => {
    el.classList.add('vbox', 'gap-2', 'p-2');
    if (el.getAttribute('data-borderless') !== 'true') el.classList.add('inset-shadow-[0_1px_var(--sidebar-border)]');
    el.setAttribute('data-slot', 'sidebar-footer');
  });
}
